<template>
    <section class="shadow-panel" aria-labelledby="mail-shadow-title">
        <div class="shadow-heading">
            <div class="d-flex align-center flex-wrap">
                <v-icon small color="purple lighten-3" class="mr-2">mdi-robot-outline</v-icon>
                <h2 id="mail-shadow-title" class="overline purple--text text--lighten-3 mb-0">Analisi automatica v.2</h2>
                <v-chip x-small outlined color="purple lighten-3" class="ml-2">Solo admin · confronto</v-chip>
            </div>
            <v-btn
                v-if="state === 'idle' || state === 'error'"
                small
                outlined
                color="purple lighten-3"
                :loading="loading"
                @click="start"
            >Avvia analisi</v-btn>
            <v-btn
                v-else-if="state === 'pending' && !polling"
                small
                text
                color="purple lighten-3"
                :loading="loading"
                @click="refresh"
            >Aggiorna risultato</v-btn>
        </div>

        <p v-if="state === 'idle'" class="body-2 mb-0">
            Avvia il confronto per questa mail. L’analisi v.2 non modifica la proposta sopra né lo stato della segnalazione.
        </p>
        <p v-else-if="state === 'pending'" class="body-2 mb-0" role="status">
            <v-progress-circular indeterminate size="16" width="2" color="purple lighten-3" class="mr-2" />
            TEV/Qwen in elaborazione. Puoi continuare a lavorare sulla mail.
        </p>
        <p v-else-if="state === 'error'" class="body-2 mb-0" role="status">
            {{ errorMessage }}
        </p>
        <div v-else-if="state === 'ready'">
            <div class="subtitle-2 font-weight-bold">{{ conclusion }}</div>
            <div class="shadow-verdicts mt-2">
                <div><strong>TEV</strong><span>{{ verdictLabel(result.tev, "tev") }}</span></div>
                <div><strong>Qwen</strong><span>{{ verdictLabel(result.qwen, "qwen") }}</span></div>
            </div>
            <p class="caption mb-0 mt-2">
                v.2 valuta solo se il testo sembra un reclamo, una comunicazione informativa o un caso ambiguo.
                Non verifica allegati, cliente, articoli o quantità. La decisione resta all’operatore.
            </p>
        </div>
    </section>
</template>

<script>
const LABELS = {
    complaint: "Possibile reclamo",
    informational: "Probabile comunicazione informativa",
    ambiguous: "Ambiguo: verifica manuale",
};

export default {
    name: "MailSegnalazioneShadow",
    props: {
        uid: { type: String, required: true },
    },
    data: () => ({
        state: "idle",
        result: {},
        loading: false,
        polling: false,
        errorMessage: "",
        pollTimer: null,
        pollCount: 0,
        active: true,
    }),
    computed: {
        conclusion() {
            const qwen = this.result.qwen;
            const tev = this.result.tev;
            const verdict = LABELS[qwen] ? qwen : (LABELS[tev] ? tev : null);
            return verdict ? `Sintesi v.2: ${LABELS[verdict]}` : "Nessuna conclusione disponibile";
        },
        endpoint() {
            return `/qualita/mail-segnalazioni/${encodeURIComponent(this.uid)}/shadow/`;
        },
    },
    mounted() {
        this.refresh();
    },
    beforeDestroy() {
        this.active = false;
        window.clearTimeout(this.pollTimer);
    },
    methods: {
        verdictLabel(value, model) {
            if (LABELS[value]) return LABELS[value];
            if (value === "not_requested") return model === "tev"
                ? "Non interrogato: testo lungo, passato a Qwen"
                : "Non interrogato: TEV ha già dato una risposta";
            return "Risultato non disponibile";
        },
        applyResult(payload) {
            if (!this.active) return;
            if (payload?.state === "ready") {
                this.state = "ready";
                this.result = payload;
                this.polling = false;
            } else if (payload?.state === "pending") {
                this.state = "pending";
                this.schedulePoll();
            } else {
                const hadFailed = this.state === "pending" || this.state === "error";
                this.state = hadFailed ? "error" : "idle";
                this.polling = false;
                if (hadFailed && !this.errorMessage) {
                    this.errorMessage = "Analisi v.2 non disponibile. La proposta attuale resta invariata.";
                }
            }
        },
        schedulePoll() {
            window.clearTimeout(this.pollTimer);
            if (this.pollCount >= 50) {
                this.polling = false;
                return;
            }
            this.polling = true;
            this.pollTimer = window.setTimeout(() => {
                this.pollCount += 1;
                this.refresh();
            }, 3000);
        },
        async refresh() {
            this.loading = true;
            try {
                const response = await this.$work.get(this.endpoint, {}, false, null, {
                    ignoreGlobalErrorStatuses: [403, 404],
                    ignoreSentryStatuses: [403, 404],
                });
                this.applyResult(response?.data);
            } catch (_) {
                if (this.active) {
                    this.state = "error";
                    this.polling = false;
                    this.errorMessage = "Impossibile leggere l’analisi v.2. Riprova tra poco.";
                }
            } finally {
                if (this.active) this.loading = false;
            }
        },
        async start() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await this.$work.post(this.endpoint, null, true);
                if (response?.data?.state === "unavailable") {
                    this.state = "error";
                    this.errorMessage = "Pilot TEV/Qwen non disponibile ora. La proposta attuale resta invariata.";
                    return;
                }
                this.pollCount = 0;
                this.applyResult(response?.data);
            } catch (_) {
                if (this.active) {
                    this.state = "error";
                    this.errorMessage = "Impossibile avviare TEV/Qwen. Riprova tra poco.";
                }
            } finally {
                if (this.active) this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.shadow-panel {
    margin: 12px 0 20px;
    padding: 18px 20px;
    border: 1px solid rgba(179, 157, 219, 0.35);
    border-left: 4px solid #b39ddb;
    border-radius: 4px;
    background: rgba(103, 58, 183, 0.06);
}
.shadow-heading, .shadow-verdicts, .shadow-verdicts > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
}
.shadow-heading { margin-bottom: 12px; }
.shadow-verdicts > div { justify-content: flex-start; }
.shadow-verdicts strong { min-width: 46px; }
</style>
